import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Modal } from "../../../components/ui";
import switchToDesktopArt from "../../../assets/svg/Dsahboard/switch-to-desktop-illustration.webp";

export interface SwitchToDesktopModalProps {
  /** Closes the note. The backdrop, Escape and the X all land here. */
  onDismiss: () => void;
}

/**
 * Whether a picture is ready to be painted, not merely fetched.
 *
 * This panel is a headline, a sentence and an illustration, and it is read in
 * one glance on the way past. Dropping the `<img>` into the tree and letting it
 * arrive whenever it arrives spends that glance twice: the words land, the card
 * is short, and then the artwork turns up under them and the card grows. Two
 * things happening is read as the panel still opening — which is what a drawer
 * does, and this is not one.
 *
 * So the waiting is done before there is anything on screen to disturb. By the
 * time the panel goes up the bitmap is in the memory cache, and the `<img>`
 * below paints on its first frame along with everything beside it.
 */
const useArtReady = (src: string): boolean => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    const settle = () => {
      if (live) setReady(true);
    };

    const art = new Image();
    art.src = src;

    // `decode()` is the only thing that answers "ready to paint" rather than
    // "downloaded" — a webp that has arrived but not been decoded still costs a
    // frame or two on a phone, which is the beat this is here to remove. Where
    // it is not implemented there is nothing to wait on and nothing to hold the
    // note back for, so the panel goes up at once; that is also what keeps this
    // out of the way of the tests, which run on a DOM that loads no images.
    if (typeof art.decode !== "function") {
      settle();
      return;
    }

    // Either answer lets the panel up. A picture that will not decode leaves a
    // note without its illustration, which is a far smaller loss than an
    // arrival that was never spoken to.
    art.decode().then(settle, settle);

    // And so does no answer at all. Longer than a bundled 12KB asset can
    // plausibly take, short enough that nobody is left looking at a dashboard
    // waiting on a dialog they cannot see.
    const giveUp = window.setTimeout(settle, 1200);

    return () => {
      live = false;
      window.clearTimeout(giveUp);
    };
  }, [src]);

  return ready;
};

/**
 * The note a phone meets as the dashboard lands: this app is at its best on a
 * desktop.
 *
 * Said once a sign-in and then got out of the way — `useDesktopNotice` owns
 * when that moment is and how often it comes round. Nothing here is a gate:
 * every screen has a phone layout and all of them work, so this is a
 * recommendation about where the work is easier, not a door. Which is why
 * there is no button under it to agree with and no way to be stuck behind it —
 * the X, the dimming and Escape all do the same thing.
 *
 * A dialog that floats near the bottom of the screen rather than a
 * `BottomSheet`, which is the other thing in this app down there. A sheet is
 * full width, square along the bottom and wears a grabber, and all three
 * promise a drawer — something with more behind it, to be pulled about and
 * pushed back. This is a card that is finished the moment it is read; it is
 * low on the screen because that is where a thumb is, and it keeps its margins
 * on every side so nothing about it suggests it is attached to an edge. See
 * `Modal`'s `align`.
 *
 * It arrives whole, which is the rest of not being a drawer: it pops into place
 * rather than climbing — see `dialog-pop` — and it waits on its own artwork, so
 * the picture lands with the words instead of a beat behind them.
 *
 * No heading bar: the headline and the sentence under it are the whole message,
 * and a title row saying "Switch to desktop" above a panel that says it larger
 * would be the same thing twice. It is still named to a screen reader — see
 * `Modal`'s `hideTitle`.
 */
const SwitchToDesktopModal: React.FC<SwitchToDesktopModalProps> = ({
  onDismiss,
}) => {
  const artReady = useArtReady(switchToDesktopArt);

  // Nothing on screen at all until the picture can go up with the rest of it.
  // What the dashboard asked for is still true in the meantime, so the brand
  // dialog this stands in front of stays put rather than flashing up in the gap.
  if (!artReady) return null;

  return (
    <Modal
      open
      hideTitle
      align="bottom"
      title="Switch to desktop"
      onClose={onDismiss}
      // The dimming dismisses, which the brand dialog's does not: that one is a
      // question with two answers, and a stray press would spend the only showing
      // there was. This is told rather than asked — a press outside it means the
      // same as a press on the X, and so does Escape.
      //
      // 560px is the width a `BottomSheet` stops at, so the two panels that stand
      // on this edge are the same size on the widest phone.
      className="max-w-[560px]"
    >
      {/* Undoes `Modal`'s body padding, so the artwork can run to the panel's own
          edges — the copy keeps its own padding below. The negative margins are
          exactly the padding they cancel, so nothing overflows. */}
      <div className="-mx-4 -my-4 sm:-mx-6 sm:-my-5">
        <div className="px-8 sm:px-10 pb-8 pt-4">
          {/* The way out, and the only control on the panel. Pulled into the
              padding by half its own box so the glyph — not the 40px target
              around it — lines up with the gutter the headline starts from. */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Close"
              className="-mr-2 flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors active:bg-[#F1F4F6] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              <X aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>

          {/* The largest type in the app, and deliberately: this is read in one
              glance on the way past, and the panel has nothing else on it
              competing for that glance.

              The odd sizes are the design's own, to the hundredth — the two
              blocks are a 2:1 scale off one base and rounding them to 29 and 15
              pulls them apart. No family: Manrope is the default everywhere, and
              no tracking: the design sets none, which is what this inherits.

              29.32px holds "Switch to desktop" on one line down to a 360px
              screen — about 250px of the 272px these gutters leave inside a
              panel itself held 12px off each edge. Re-measure if the words or the
              size change, since a second line here pushes the artwork off the
              bottom of a short phone. */}
          <h3 className="mt-2 text-[29.32px] font-medium leading-[1.25] text-[#364E51]">
            Switch to desktop
          </h3>

          <p className="mt-4 text-[14.66px] font-normal leading-[1.5] text-[#718184]">
            Naurist is designed for desktop to deliver the best experience, making
            it easier to manage everything in one place.
          </p>
        </div>

        {/* Decorative, hence the empty alt — it is a picture of the screen the
            sentence above has just described, and reading it out would say the
            same thing a third time.

            Run to the panel's own edges and cut off by them, which is how the
            asset is already drawn: its canvas is cropped hard on the right and
            bottom, so the desktop in it carries on past the card rather than
            sitting framed inside it. Dropping it in at the panel's full width is
            all that takes — the panel's rounded corners clip it, so the picture
            ends in the same curve the card does.

            Its own pixel size is given so the box is the right height on the
            first layout pass rather than after the picture turns up, and the
            decode is asked for synchronously: `useArtReady` has already paid
            for it, so there is nothing left to gain by letting the browser
            paint the card one frame ahead of its picture. */}
        <img
          src={switchToDesktopArt}
          alt=""
          width={834}
          height={500}
          decoding="sync"
          className="block h-auto w-full"
        />
      </div>
    </Modal>
  );
};

export default SwitchToDesktopModal;
